import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { chatPush } from '../utils/live/wsTransport';

/*
 * Pokes from the chat's socket (utils/live/wsTransport.js) for as long as a
 * component listens: `onPoke(poke)` hears each one for `channelId` (or every
 * one, with none), and the hook answers whether the socket is open -- the
 * listener's cue to slow its polling to the safety net.
 *
 *   const live = useChatPush({ channelId, onPoke: () => pokeRef.current?.() });
 *   const poke = useLiveChannel(pull, onData, { base: live ? SAFETY_NET_MS : 4000, ... });
 *
 * ChatPushContext hands a component another transport (a test's fake); by
 * default it is the app's one.
 */
export const ChatPushContext = createContext(null);

export default function useChatPush({ enabled = true, channelId = null, onPoke } = {}) {
  const push = useContext(ChatPushContext) || chatPush();
  const [open, setOpen] = useState(() => push.state === 'open');
  const onPokeRef = useRef(onPoke);
  onPokeRef.current = onPoke;

  useEffect(() => {
    if (!enabled) return undefined;
    const unwatch = push.onState((state) => setOpen(state === 'open'));
    const unsubscribe = push.subscribe({ channelId, onPoke: (poke) => onPokeRef.current?.(poke) });
    setOpen(push.state === 'open');
    return () => {
      unsubscribe();
      unwatch();
    };
  }, [push, enabled, channelId]);

  return enabled && open;
}
